import PropTypes from 'prop-types'
import { createContext, memo, useContext, useMemo, useState } from 'react'
import noop from 'lib/util/noop'
import useCookies from './hooks/useCookies'
import useInterface from './hooks/useInterface'
import useLoaders from './hooks/useLoaders'
import useMongo from './hooks/useMongo'
import useRoutes from './hooks/useRoutes'
import useTheme from './hooks/useTheme'

// The context
export const RootContext = createContext({
  loginNFTDrops: noop
})

// Use root context utility
export const useRootContext = () => {
  const context = useContext(RootContext)

  return context
}

/**
 * The `RootProvider` is the primary react context provider
 * for the application
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the react element
 */
function RootProvider (props) {
  const { children } = props
  const [appActivityStatus, setAppActivityStatus] = useState(null)
  const [appActivityLabel, setAppActivityLabel] = useState(null)
  const { appData, loadApplication } = useLoaders()
  const { hasCookieConsent, onAcceptCookies } = useCookies()
  const { setTheme, theme } = useTheme()
  const { loginRealm } = useMongo()
  const {
    addBlocker,
    isRouteTransitioning,
    removeBlocker,
    route,
    setDocumentTitle
  } = useRoutes(appData)
  const {
    headerMode,
    isBurgerMenuOpen,
    isCartOpen,
    isHeaderActive,
    isHeaderLogoDisabled,
    setIsBurgerMenuOpen,
    setIsCartOpen,
    setIsHeaderLogoDisabled
  } = useInterface(appData, route, isRouteTransitioning)

  const ctx = useMemo(
    () => ({
      addBlocker,
      appActivityLabel,
      appActivityStatus,
      appData,
      hasCookieConsent,
      headerMode,
      isBurgerMenuOpen,
      isCartOpen,
      isHeaderActive,
      isHeaderLogoDisabled,
      loadApplication,
      loginRealm,
      onAcceptCookies,
      removeBlocker,
      route,
      setAppActivityLabel,
      setAppActivityStatus,
      setDocumentTitle,
      setIsBurgerMenuOpen,
      setIsCartOpen,
      setIsHeaderLogoDisabled,
      setTheme,
      theme
    }),
    [
      appActivityLabel,
      appActivityStatus,
      appData,
      hasCookieConsent,
      headerMode,
      isBurgerMenuOpen,
      isCartOpen,
      isHeaderActive,
      isHeaderLogoDisabled,
      loadApplication,
      loginRealm,
      onAcceptCookies,
      route,
      setDocumentTitle,
      setTheme,
      theme
    ]
  )

  return <RootContext.Provider value={ctx}>{children}</RootContext.Provider>
}

/** @type {object} */
RootProvider.propTypes = {
  children: PropTypes.node
}

/** @type {object} */
RootProvider.defaultProps = {
  children: []
}

// Memoize
export default memo(RootProvider)
